import React from 'react';
import { v4 as uuid } from 'uuid';
export const useMultiSelect = ({ values, options, isDisabled, isRequired, }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const { current: buttonID } = React.useRef(uuid());
    const checkboxRef = React.useRef(null);
    const multiSelectWrapperRef = React.useRef(null);
    const selectMenuButtonRef = React.useRef(null);
    // storing values in a set prevents duplication of values
    const safeValues = React.useMemo(() => new Set(values), [values]);
    React.useEffect(() => {
        if (isDisabled)
            setIsOpen(false);
    }, [isDisabled, setIsOpen]);
    React.useEffect(() => {
        const getIsInside = (e) => {
            var _a;
            return (e.target === multiSelectWrapperRef.current ||
                (e.target instanceof Node &&
                    !!((_a = multiSelectWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))));
        };
        const detectClick = function detectClick(e) {
            const isInside = getIsInside(e);
            if (!isInside)
                setIsOpen(false);
        };
        const detectEscape = function detectEscape(e) {
            var _a;
            const isInside = getIsInside(e);
            const isEscape = e.key === 'Escape';
            if (isInside && isEscape) {
                setIsOpen(false);
                (_a = selectMenuButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        window.document.addEventListener('click', detectClick);
        window.document.addEventListener('keydown', detectEscape);
        return () => {
            window.document.removeEventListener('click', detectClick);
            window.document.removeEventListener('keydown', detectEscape);
        };
    }, []);
    const itemLabels = React.useMemo(() => options.reduce((acc, { label: optionLabel, value }) => {
        return Object.assign(Object.assign({}, acc), { [value]: optionLabel });
    }, {}), [options]);
    const optionValues = React.useMemo(() => {
        const allValues = options.map(({ value }) => {
            return String(value);
        });
        const enabledAndDisabledValues = options.reduce((acc, { value, isDisabled: disabled }) => {
            if (disabled) {
                return Object.assign(Object.assign({}, acc), { isDisabled: [...acc.isDisabled, String(value)] });
            }
            return Object.assign(Object.assign({}, acc), { isEnabled: [...acc.isEnabled, String(value)] });
        }, { isEnabled: [], isDisabled: [] });
        return Object.assign({ all: new Set(allValues) }, enabledAndDisabledValues);
    }, [options]);
    // Ensures the items in values match the original order of the consumer-provided optionValues.
    // This, in turn, ensures that when a user selects or deselects items, the name that is displayed
    // will correspond to the order of the items the consumer originally passed.
    const valuesArray = React.useMemo(() => Array.from(safeValues).sort((a, b) => {
        const aIndex = Array.from(optionValues.all).indexOf(a);
        const bIndex = Array.from(optionValues.all).indexOf(b);
        return aIndex - bIndex;
    }), [safeValues, optionValues]);
    const getSelectMenuButtonStrings = () => {
        const requiredText = isRequired ? 'Required.' : '';
        if (!valuesArray.length)
            return { text: '', ariaDescribedBy: requiredText };
        if (valuesArray.length === 1) {
            const content = itemLabels[valuesArray[0]];
            return { text: content, ariaDescribedBy: `${content}. ${requiredText}` };
        }
        const selectedItem = itemLabels[valuesArray[0]];
        const additionalItemCount = valuesArray.length - 1;
        const greaterThanOne = additionalItemCount > 1;
        return {
            text: `${selectedItem} (+ ${additionalItemCount} more)`,
            ariaDescribedBy: `${selectedItem} and ${additionalItemCount} more ${greaterThanOne ? 'items' : 'item'} selected. ${requiredText}`,
        };
    };
    const { text: selectMenuButtonText, ariaDescribedBy: selectMenuButtonAriaDescribedBy, } = getSelectMenuButtonStrings();
    return {
        isOpen,
        setIsOpen,
        buttonID,
        selectMenuButtonText,
        selectMenuButtonAriaDescribedBy,
        checkboxRef,
        multiSelectWrapperRef,
        selectMenuButtonRef,
        safeValues,
        optionValues,
    };
};
//# sourceMappingURL=useMultiSelect.js.map